import { useCallback, useEffect, useState } from 'react';
import { useReactFlow } from '@xyflow/react';
import { SweepCanvasRfNode } from '../../canvas-types/nodeTypesData';
import { RFEdgeFloatingEdge } from '../../edges';
import { useMultipleNodesSelected } from './useMultipleNodesSelected';
import { useRichTextEditor } from '../../../common/rich-text-editor/RichTextEditorProvider';

export interface UseNodeRichTextEditBehaviorParams {
  id: string;
  freshlyCreated?: boolean;
  selected?: boolean;
  dragging?: boolean;
  temporary?: boolean;
  trackEmptyEdits?: boolean;
  currentRichText?: string;
  height?: number;
}

export interface UseNodeRichTextEditBehaviorReturn {
  handleRichTextChange: (newRichText: string) => void;
  lastEditWasEmpty: boolean;
  editMode: boolean;
  exclusivelySelected: boolean;
  setEditMode: (editMode: boolean) => void;
  multipleNodesSelected: boolean;
  saveRichText: (textScale: number, observedHeight?: number) => string;
  localRichText: string;
}

/**
 * Manages rich text editing behavior for canvas nodes.
 * Handles edit mode, auto-focus for new nodes, and empty edit tracking.
 */
export const useNodeRichTextEditBehavior = ({
  id,
  freshlyCreated,
  selected,
  dragging,
  temporary,
  trackEmptyEdits = false,
  currentRichText,
}: UseNodeRichTextEditBehaviorParams): UseNodeRichTextEditBehaviorReturn => {
  const { updateNodeData, updateNode } = useReactFlow<SweepCanvasRfNode, RFEdgeFloatingEdge>();

  const [lastEditWasEmpty, setLastEditWasEmpty] = useState(false);
  const multipleNodesSelected = useMultipleNodesSelected();
  const exclusivelySelected =
    Boolean(selected) && !dragging && !multipleNodesSelected && !temporary;
  const { isSerializedStateEmpty } = useRichTextEditor();

  const [editMode, setEditMode] = useState(Boolean(freshlyCreated && exclusivelySelected));
  const [localRichText, setLocalRichText] = useState<string>(currentRichText ?? '');

  useEffect(() => {
    if (exclusivelySelected && freshlyCreated) {
      updateNodeData(id, { freshlyCreated: false, draft: false });
      setEditMode(true);
    }
  }, [id, exclusivelySelected, freshlyCreated, updateNodeData, setEditMode]);

  const saveRichText = useCallback(
    (textScale: number, observedHeight?: number) => {
      
      const nodeData = {
        richText: localRichText,
        textScale,
        temporary: false,
        draft: false,
        freshlyCreated: false,
      };
      if (observedHeight) {
        updateNode(
          id,
          {
            data: nodeData,
            height: observedHeight ?? undefined,
          },
          { replace: false },
        );
      } else {
        updateNodeData(id, nodeData, { replace: false });
      }

      if (trackEmptyEdits) {
        // Use isSerializedStateEmpty to check if the serialized state string is empty
        setLastEditWasEmpty(isSerializedStateEmpty(localRichText));
      }
      return localRichText;
    },
    [id, isSerializedStateEmpty, localRichText, trackEmptyEdits, updateNode, updateNodeData],
  );

  useEffect(() => {
    if (!exclusivelySelected) {
      setEditMode(false);
    }
  }, [exclusivelySelected]);

  return {
    handleRichTextChange: setLocalRichText,
    lastEditWasEmpty,
    editMode,
    exclusivelySelected,
    setEditMode,
    multipleNodesSelected,
    saveRichText,
    localRichText,
  };
};
